/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import cx from 'classnames';
import azCommonStyles from '@/theme/globals.module.scss';
import { useLocale } from '@/hooks/useLocale';
import styles from './styles.module.scss';
import { formatPrice } from '@/utils/validator/formatPrice';
import { getCurrencySymbol } from '@/utils/priceHelpers';
import { useLabels } from '@/hooks/useLabels';

type Props = {
  showPriceCaption: boolean;
  configurableSku: boolean | undefined;
  showDiscountedPrice: boolean;
  dollars: string;
  cents: string;
  discountedPrice: string | null | undefined;
};

const labelMap = {
  lblStartingAt: 'label_starting_at',
  lblDiscount: 'label_discount',
  lblAnd: 'label_MyAccountMyProfile_rewards_And',
  lblPrice: 'label_the_price_of_item',
  lblDollars: 'label_shelf_resultsList_price_dollars',
  lblCents: 'label_shelf_resultsList_price_cents',
};

const PriceDollarsAndCents = ({
  showPriceCaption,
  configurableSku,
  showDiscountedPrice,
  dollars,
  cents,
  discountedPrice,
}: Props) => {
  const labels = useLabels(labelMap);
  const ariaPriceOfItem = labels.lblPrice;
  const dollarsLabel = labels.lblDollars;
  const centsLabel = labels.lblCents;
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';

  const getDiscountedPrice = (): string[] => {
    let discountedPriceDollars = '0';
    let discountedPriceCents = '00';
    if (strikeThroughPricingEnabled && discountedPrice !== null) {
      const formattedDiscountedPrice = discountedPrice
        ? (formatPrice(parseFloat(discountedPrice))?.toString() ?? '0.00')
        : '0.00';
      discountedPriceDollars = formattedDiscountedPrice.split('.')[0];
      discountedPriceCents = formattedDiscountedPrice.split('.')[1] ?? '00';
    }
    return [discountedPriceDollars, discountedPriceCents];
  };

  const [discountedPriceDollars, discountedPriceCents] = getDiscountedPrice();
  const regularDollarAmount = formatPrice(parseFloat(dollars))?.toString().split('.')[0] ?? '0';
  const locale = useLocale();
  const currencySymbol = getCurrencySymbol(locale);

  // Helper function to create screen reader friendly price text
  const getScreenReaderPrice = (dollarAmount: string, centsAmount: string): string => {
    // Handle edge cases
    if (dollarAmount === '0' && centsAmount === '00') {
      return `0 ${dollarsLabel}`;
    }
    if (dollarAmount === '0') {
      return `${centsAmount} ${centsLabel}`;
    }
    if (centsAmount === '00') {
      return `${dollarAmount} ${dollarsLabel}`;
    }

    return `${dollarAmount} ${dollarsLabel} ${labels.lblAnd} ${centsAmount} ${centsLabel}`;
  };

  const currentDollars = showDiscountedPrice ? discountedPriceDollars : regularDollarAmount;
  const currentCents = showDiscountedPrice ? discountedPriceCents : cents;

  const screenReaderText = showPriceCaption
    ? `${labels.lblStartingAt} ${getScreenReaderPrice(currentDollars, currentCents)}`
    : getScreenReaderPrice(currentDollars, currentCents);

  // If showing discounted price, also include original price for screen readers
  const fullScreenReaderText =
    showDiscountedPrice && strikeThroughPricingEnabled
      ? `${screenReaderText}. ${labels.lblDiscount} ${getScreenReaderPrice(
          regularDollarAmount,
          cents,
        )}`
      : screenReaderText;

  return (
    <div
      className={cx(azCommonStyles['az-title-1-heavy'], styles.priceContainer)}
      data-testid="price-container-dollars"
    >
      <span
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: '0',
        }}
      >
        {ariaPriceOfItem} {fullScreenReaderText}
      </span>
      {showPriceCaption && (
        <span
          aria-hidden="true"
          className={cx(configurableSku ? styles.startingAt : styles.prefix, styles.lowercase)}
        >
          Starting at
        </span>
      )}
      <span
        aria-hidden="true"
        className={showDiscountedPrice ? styles.strikePrefix : styles.prefix}
      >
        {currencySymbol}
      </span>
      <span aria-hidden="true" className={showDiscountedPrice ? styles.strikePrice : styles.price}>
        {showDiscountedPrice ? discountedPriceDollars : regularDollarAmount}
      </span>
      <span aria-hidden="true" className={showDiscountedPrice ? styles.strikeDec : styles.dec}>
        {showDiscountedPrice ? discountedPriceCents : cents}
      </span>
    </div>
  );
};

export default PriceDollarsAndCents;
